<template lang="html">
  <div class="product-breadcrumbs">
    <div class="sm:hidden">
      <div class="flex items-center gap-2 rounded-lg border border-default bg-default px-2.5 py-2">
        <NuxtLink
          v-if="mobileBackItem?.to"
          :to="mobileBackItem.to"
          class="flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1 py-1 text-sm font-medium text-default transition-colors hover:text-highlighted"
        >
          <UIcon name="i-lucide-arrow-left" class="size-4 shrink-0" />
          <span class="truncate">{{ mobileBackItem.label }}</span>
        </NuxtLink>

        <button
          v-if="hasDisclosure"
          type="button"
          class="flex max-w-[55%] min-w-0 items-center gap-1 text-xs text-muted transition-colors hover:text-default"
          @click="isPathOpen = true"
        >
          <span class="truncate">{{ mobilePathLabel }}</span>
          <UIcon name="i-lucide-chevron-down" class="size-4 shrink-0" />
        </button>
      </div>

      <UModal
        v-model:open="isPathOpen"
        fullscreen
        dismissible
        :ui="{ content: 'sm:max-w-md sm:h-auto sm:rounded-xl' }"
      >
        <template #content="{ close }">
          <div class="flex h-full flex-col gap-4 p-4 sm:h-auto">
            <div class="flex items-start justify-between gap-3">
              <div class="space-y-1">
                <p class="text-lg font-semibold text-default">
                  Навигация
                </p>
                <p class="text-sm text-muted">
                  Полный путь по каталогу
                </p>
              </div>

              <UButton
                color="neutral"
                variant="ghost"
                size="sm"
                icon="i-lucide-x"
                square
                aria-label="Закрыть навигацию"
                @click="close()"
              />
            </div>

            <nav aria-label="Полный путь по каталогу">
              <ol class="space-y-2">
                <li v-for="(item, index) in items" :key="`${item.label}-${index}`">
                  <NuxtLink
                    v-if="item.to"
                    :to="item.to"
                    class="flex items-center justify-between gap-3 rounded-lg border border-default px-3 py-3 text-sm text-default transition-colors hover:text-highlighted"
                    @click="close()"
                  >
                    <span>{{ item.label }}</span>
                    <UIcon name="i-lucide-chevron-right" class="size-4 shrink-0 text-muted" />
                  </NuxtLink>

                  <div
                    v-else
                    class="rounded-lg bg-elevated px-3 py-3 text-sm font-medium text-default"
                  >
                    {{ item.label }}
                  </div>
                </li>
              </ol>
            </nav>

            <UButton
              color="neutral"
              variant="outline"
              size="lg"
              block
              class="mt-auto"
              label="Закрыть"
              @click="close()"
            />
          </div>
        </template>
      </UModal>
    </div>

    <div class="hidden overflow-x-auto sm:block">
      <UBreadcrumb
        :items="items"
        :ui="{
          root: 'text-sm text-muted',
          list: 'flex-nowrap',
          linkLabel: 'truncate max-w-40 sm:max-w-56',
        }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { BreadcrumbItem } from '@nuxt/ui'
import type { EntityRef, ProductDto } from '@repo/api/web/models'

const props = defineProps<{
  value: ProductDto
}>()

const route = useRoute()
const { routing } = useProducts()
const isPathOpen = ref(false)

const items = computed<BreadcrumbItem[]>(() => {
  const crumbs: BreadcrumbItem[] = [
    { label: 'Главная', to: '/' },
    { label: 'Каталог', to: routing.getCatalog().route },
  ]

  const categoryCrumb = resolveCategoryCrumb()
  if (categoryCrumb) {
    crumbs.push(categoryCrumb)
  }

  crumbs.push({
    label: props.value.wellKnownAttributes?.Name?.trim() || 'Товар',
  })

  return crumbs
})

const mobileTrail = computed(() => items.value.slice(1, -1))

const mobileBackItem = computed<BreadcrumbItem | null>(() => {
  return (
    [...mobileTrail.value].reverse().find(item => item.to)
    ?? items.value.find(item => item.to)
    ?? null
  )
})

const hasDisclosure = computed(() => items.value.length > 2)

const mobilePathLabel = computed(() => {
  const labels = mobileTrail.value
    .map(item => item.label)
    .filter((label): label is string => Boolean(label))

  return labels.join(' / ') || items.value.at(-1)?.label || ''
})

function resolveCategoryCrumb(): BreadcrumbItem | null {
  const categoryQuery = route.query.category

  if (categoryQuery) {
    const slug = String(categoryQuery)
    const categoryRef = findCategoryBySlug(slug) ?? props.value.categories?.[0]

    return {
      label: categoryRef?.displayName?.trim() || 'Категория',
      to: routing.getCatalogBySlug(slug).route,
    }
  }

  const category = props.value.categories?.[0]
  if (!category) {
    return null
  }

  return {
    label: category.displayName?.trim() || 'Категория',
    to: routing.getCatalog(category).route,
  }
}

function findCategoryBySlug(slug: string): EntityRef | undefined {
  const categoryId = routing.extractIdFromSlug(slug)

  return props.value.categories?.find(category => category.id === categoryId)
}
</script>
